import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Search } from 'lucide-react';
import type { SearchResult } from '@spawnwire/shared';
import { Button, EmptyState, Input, Select, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { channelPath, navigate } from '../../lib/nav';
import { mentionableUsers } from '../../lib/mentionText';
import { useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { MessagePreview } from './MessagePreview';
import { PanelHeader } from './PanelHeader';

const PAGE = 25;

/**
 * Supports a few filters inline: from:username, in:#channel, has:image|file|link
 * and pinned:true. Everything else is matched as word prefixes.
 */
function parseQuery(raw: string, channelId: string) {
  const state = useStore.getState();
  const channel = state.channels[channelId];
  const params = new URLSearchParams();
  const words: string[] = [];
  for (const token of raw.split(/\s+/).filter(Boolean)) {
    const m = /^(from|in|has|pinned|mentions):(.+)$/i.exec(token);
    if (!m) {
      words.push(token);
      continue;
    }
    const [, key, value] = m;
    if (key === 'from' || key === 'mentions') {
      const user = mentionableUsers(state, channelId).find((u) => u.username === value!.replace(/^@/, '').toLowerCase());
      if (user) params.set(key === 'from' ? 'authorId' : 'mentions', user.id);
      else words.push(token);
    } else if (key === 'in') {
      const target = Object.values(state.channels).find((c) => c.spaceId === channel?.spaceId && c.name === value!.replace(/^#/, ''));
      if (target) params.set('channelId', target.id);
    } else if (key === 'has' && ['image', 'file', 'link'].includes(value!)) {
      params.set('has', value!);
    } else if (key === 'pinned') {
      params.set('pinned', value === 'true' ? 'true' : 'false');
    }
  }
  params.set('q', words.join(' '));
  return params;
}

export function SearchPanel({ channelId }: { channelId: string }) {
  const initial = useUi((s) => s.searchQuery);
  const spaceId = useStore((s) => s.channels[channelId]?.spaceId ?? null);
  const [query, setQuery] = useState(initial);
  const [scope, setScope] = useState<'space' | 'channel'>(spaceId ? 'space' : 'channel');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(q: string, at = 0) {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = parseQuery(q, channelId);
      if (!params.get('channelId')) {
        if (scope === 'channel') params.set('channelId', channelId);
        else if (spaceId) params.set('spaceId', spaceId);
      }
      params.set('limit', String(PAGE));
      params.set('offset', String(at));
      const res = await http.get<SearchResult>(`/search?${params}`);
      setResult(res);
      setOffset(at);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  // Run the query handed over from the header search box (latest `run` via ref).
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    setQuery(initial);
    if (initial) void runRef.current(initial);
  }, [initial]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(query);
  };

  return (
    <>
      <PanelHeader title="Search" icon={<Search className="size-5 text-faint" aria-hidden />} />
      <form onSubmit={submit} className="space-y-2 border-b border-line p-3">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search messages" aria-label="Search query" autoFocus />
        <div className="flex gap-2">
          {spaceId && (
            <Select
              value={scope}
              onChange={setScope}
              options={[
                { value: 'space', label: 'This space' },
                { value: 'channel', label: 'This channel' },
              ]}
              className="h-8"
            />
          )}
          <Button type="submit" size="sm" loading={loading}>
            Search
          </Button>
        </div>
        <p className="text-[11px] text-faint">Filters: from:username, in:#channel, has:image, has:file, has:link, pinned:true</p>
      </form>
      <div className="scroll-thin min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {error && <p className="text-sm text-danger">{error}</p>}
        {loading && !result && (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        )}
        {result && result.total === 0 && <EmptyState icon={Search} title="No results" body="Try fewer words or a different filter." />}
        {result && result.total > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-faint">
              {result.total} result{result.total === 1 ? '' : 's'}
            </p>
            {result.messages.map((m) => {
              const c = useStore.getState().channels[m.channelId];
              return (
                <MessagePreview
                  key={m.id}
                  message={m}
                  context={c ? (c.spaceId ? `#${c.name}` : 'Direct message') : undefined}
                  actions={
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        if (c && m.channelId !== channelId) navigate(`${channelPath(c)}?m=${m.id}`);
                        else ui.jumpTo(m.channelId, m.id);
                      }}
                    >
                      Jump
                    </Button>
                  }
                />
              );
            })}
            <div className="flex justify-between pt-2">
              <Button size="sm" variant="ghost" disabled={offset === 0 || loading} onClick={() => void run(query, Math.max(0, offset - PAGE))}>
                Previous
              </Button>
              <Button size="sm" variant="ghost" disabled={offset + PAGE >= result.total || loading} onClick={() => void run(query, offset + PAGE)}>
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
